import { ComponentRef, Injectable } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subject } from 'rxjs';

import {
  NotificationsLocation
} from '../../../services/notifications/notifications-preferences.domain';
import { INotificationComponent, NotificationOverlayConfiguration, NotificationOverlayRef } from '../notifications-toast.domain';

/**
 * Notifications Toast Service subscribe to websocket
 * Is iresposibale for creating and destroying NotificationsToastContainerComponent
 *
 * Acutal toast need to set by delegate function 'setToast'
 *
 */

@Injectable({
  providedIn: 'root'
})
export class NotificationsOverlayService {

  readonly onClose: Subject<void>;

  constructor(private overlay: Overlay) {
    this.onClose = new Subject<void>();
  }

  /**
   * Opens notifications
   */
  open(config: NotificationOverlayConfiguration): NotificationOverlayRef {
    const overlayRef = this.createOverlay(config.notificationLocation, config.margin);
    const componentRef = this.attachNotifications(overlayRef, config.containerPortal);
    const ref = {
      overlayRef,
      componentRef
    };
    const component = componentRef.instance;
    component.onClose?.subscribe((): void => {
      this.close(ref);
    });
    return ref;
  }

  /**
   * Closes notifications
   */
  close(ref: NotificationOverlayRef): void {
    if (ref !== undefined) {
      ref.componentRef?.destroy();
      ref.overlayRef?.dispose();
      ref.overlayRef = undefined;
      this.onClose.next();
    }
  }

  /**
   * Creates the overlay into which the dialog will be loaded.
   *
   * @returns A promise resolving to the OverlayRef for the created overlay.
   */
  private createOverlay(notificationLocation: NotificationsLocation, margin: string): OverlayRef {
    return this.overlay.create(this.getOverlayConfig(notificationLocation, margin));
  }

  /**
   * @returns The overlay configuration.
   */
  private getOverlayConfig(notificationLocation: NotificationsLocation, margin: string): OverlayConfig {
    let positionStrategy: PositionStrategy;
    switch (notificationLocation) {
      case NotificationsLocation.CENTER:
        positionStrategy = this.overlay.position().global().bottom(margin).centerHorizontally();
        break;
      case NotificationsLocation.LEFT:
        positionStrategy = this.overlay.position().global().bottom(margin).left(margin);
        break;
      default:
        positionStrategy = this.overlay.position().global().bottom(margin).right(margin);
    }

    return new OverlayConfig({
      panelClass: 'td-wb-overlay-panel-fixed-position',
      positionStrategy,
      hasBackdrop: false
    });
  }

  /**
   * Attaches notification component to the overlay
   */
  private attachNotifications(overlayRef: OverlayRef,
                              containerPortal: ComponentPortal<INotificationComponent>): ComponentRef<INotificationComponent> {
    return overlayRef.attach(containerPortal);
  }
}
